import { ApiNode, RemoteDataSimple } from "types";

import { Node } from "react-checkbox-tree";
import classNames from "classnames";

/**
 * Determines the "selections" that should be displayed based off the full tree
 * as well as the "checked" nodes.
 *
 * This implementation expects that the `checked` array only includes a parent node
 * when all of its children are also checked.
 * You can get this behavior from `react-checkbox-tree` by setting `checkModel` to "all".
 * Our wrapped version does this out of the box.
 *
 * Optimization thoughts:
 * - We really only need to search one root level selectable node per call.
 *   For example if you click on `1:26:35` we could start this search at `1` (I think).
 * - Recursion may be a bad idea in here - possible to stack overflow?
 * - Maybe could track a copy of `checked` that we deplete as we build the pills
 *   maybe we can exit early once all "checked" entries are accounted for
 *   thinking this could save us from walking other branches if we handle all checked early on
 */
export const buildSelections = (nodes: Node[], checked: string[], result: Node[] = []): Node[] => {
  const checkedSet = new Set(checked);
  const visit = (visitedNodes: Node[]) => {
    visitedNodes.forEach((node) => {
      if (checkedSet.has(node.value)) {
        result.push(node);
      } else {
        visit(node.children ?? []);
      }
    });
  };
  visit(nodes);
  return result;
};

const isNodeMatch = (node: Node, loweredFilterText: string): boolean =>
  !!node.label?.toString()?.toLowerCase().includes(loweredFilterText);

/**
 * Filter the tree by applying CSS.
 *
 * This results in _the exact same nodes_ we just apply className based on how they match the search text.
 * We maintain the same nodes so that selections within the underlying tree control are preserved.
 * If you give the tree control a filtered set and someone makes a selection, then the "checked"
 * array provided by the tree only includes selections within the filtered set.
 * While we could probably write code to handle that, it seems simpler to just maintain the full
 * set of nodes and visually hide the ones that are not matched.
 *
 * We apply the following classes when filtering is present:
 *
 * `d-none` - node and ALL children do not match, hide it
 * `visible` - node or at least one child matches, display it
 * `partial-match` - node itself does not match, but at least one child does
 *                   this is applied _in addition_ to `visible` and allows us to style partial matches in the UI
 *
 * This can most likely be optimized but we plan to debounce calls to it.
 * We can see how it performs and adjust from there.
 *
 * One thought is that we re-check children for visibility to determine if the parent is visible.
 * If we switched this to a reduce call we could track the visibility along the way
 * and continuously roll it up to a single value.
 * Again, we can see how this performs and decide if that is necessary.
 */
export const filterNodesByCss = (nodes: Node[], filterText: string): [boolean, Node[]] => {
  if (!filterText || filterText.length <= 1) {
    return [true, nodes];
  }

  const lowerFilter = filterText.toLocaleLowerCase();

  const checkVisible = (node: Node): Node => {
    // ## Leaf nodes...
    if (!node.children || node.children.length === 0) {
      // only return true here if the filter text matches
      const isMatch = isNodeMatch(node, lowerFilter);
      const className = classNames({
        visible: isMatch,
        "d-none": !isMatch,
      });
      return { ...node, className };
    }

    // ## Nodes with children

    // first calculate the visibility of the children
    const children = node.children.map(checkVisible);

    // if this node matches the search or at least one child - show it
    // short circuit on this node matching in case can skip looping the children again
    const isMatch = isNodeMatch(node, lowerFilter);
    const isVisible = isMatch || children.some((x) => x.className?.includes("visible"));
    const className = classNames({
      visible: isVisible,
      "partial-match": !isMatch && isVisible,
      "d-none": !isVisible,
    });
    return { ...node, className, children };
  };

  const filteredNodes = nodes.map(checkVisible);
  const areNodesVisible = !!filteredNodes.find((x) => x.className?.includes("visible"));
  return [areNodesVisible, filteredNodes];
};

/**
 * Builds `{ placeholder, disabled }` based on the provided `RemoteDataSimple`.
 *
 * `placeholder` is only included while loading/erroring, so spreading this after an
 * explicit `placeholderItem` prop overrides the templated placeholder temporarily
 * without clobbering it once `remoteData` settles.
 */
export const getLoadingProps = (remoteData: RemoteDataSimple, placeholderItem: string) => {
  if (remoteData.isLoading) {
    return { disabled: true, placeholder: `Loading ${placeholderItem}...` };
  }

  if (remoteData.error) {
    return { disabled: true, placeholder: `Error loading ${placeholderItem}` };
  }

  return { disabled: false };
};

/**
 * The delimiter used to join a node's own id/name onto its parent's encoded value.
 */
const VALUE_DELIMITER = ".";

/**
 * Builds a `value` for a node in a path-encoded tree by prefixing `id` with its parent's value.
 *
 * example: `encodeNodeValue("verticals.retail", 3483)` -> `"verticals.retail.3483"`
 *
 * This lets `TreeSearch`'s pill removal (which matches via `startsWith`) remove an entire
 * group's descendants when the group's own combined pill is removed, and mirrors the scheme
 * `taxonomyNodeUtils` uses for the taxonomy tag tree.
 */
export const encodeNodeValue = (parentValue: string | undefined, id: string | number): string =>
  parentValue ? `${parentValue}${VALUE_DELIMITER}${id}` : String(id);

/**
 * Extracts unique numeric IDs from an array of path-encoded node values (see `encodeNodeValue`).
 *
 * Only the last (most specific) `.`-separated segment of each value is considered, so
 * ancestor/group segments - including a pseudo (`null` id) node's own value, which
 * `checkModel="all"` adds to `checked` once every child is checked - are dropped.
 *
 * example: `decodeNodeIds(["verticals.retail.3483", "Internal"])` -> `["3483"]`
 */
export const decodeNodeIds = (checked: string[]): string[] => {
  const ids = checked.map((value) => value.split(VALUE_DELIMITER).pop() ?? "").filter((value) => /^-?\d+$/.test(value));
  return Array.from(new Set(ids));
};

/**
 * Maps the response to the `Node[]` shape expected by `TreeSearch`.
 * Grouping pseudo nodes that have a `null` id and are not selectable themselves.
 */
export const mapToNodes = (nodes: ApiNode[], parentValue?: string): Node[] =>
  nodes.map((node) => {
    const value = node.id !== null ? encodeNodeValue(parentValue, node.id) : node.name;
    return {
      value,
      label: node.name,
      showCheckbox: node.id !== null,
      children: node.children.length > 0 ? mapToNodes(node.children, value) : undefined,
    };
  });
